import { useMemo } from 'react'

import { cn } from 'utils'
import { type EmployersEnrollmentStatusCounts } from 'utils/web_api.schemas'

import { type DisplayedEnrollmentStatus, ENROLLMENT_STATUS_ORDER } from './EnrollmentTracker'

const BAR_SEGMENT_STYLE: Record<Exclude<DisplayedEnrollmentStatus, 'not_invited'>, React.CSSProperties> = {
  enrolled: {
    backgroundColor: '#29845A',
  },
  viewing_plans: {
    background: 'repeating-linear-gradient(-45deg, #29845A 0, #29845A 1px, white 1px, white 3px)',
    border: '1px solid #29845A',
  },
  invite_sent: {
    backgroundColor: 'transparent',
    border: '1px solid #29845A',
  },
  declined: {
    backgroundColor: '#919191',
  },
}

type Props = {
  enrollmentStatusCounts: EmployersEnrollmentStatusCounts | undefined,
  totalNumEmployeesInvited: number,
  // Uses DisplayedEnrollmentStatus since hover state is shared with legend (which includes not_invited)
  hoveredStatus: DisplayedEnrollmentStatus | null,
  setHoveredStatus: (status: DisplayedEnrollmentStatus | null) => void,
  isLoading: boolean,
}

export default function StackedBarChart (props: Props) {
  const {
    enrollmentStatusCounts,
    hoveredStatus,
    setHoveredStatus,
    isLoading,
  } = props

  const enrollmentStatusesGreaterThanZero = useMemo(() => {
    if (!enrollmentStatusCounts) return []
    // Use ENROLLMENT_STATUS_ORDER but exclude 'not_invited' (not part of invited employees)
    return ENROLLMENT_STATUS_ORDER
      .filter((key): key is Exclude<DisplayedEnrollmentStatus, 'not_invited'> => key !== 'not_invited')
      .filter((key) => enrollmentStatusCounts[key] > 0)
  }, [enrollmentStatusCounts])

  if (isLoading) {
    return (
      <div className='flex gap-1 w-full h-3'>
        <div className='flex-1 rounded bg-gray-200 animate-pulse' />
      </div>
    )
  }

  return (
    <div className='flex gap-1 w-full h-3'>
      {enrollmentStatusesGreaterThanZero.map((key) => {
        const numForStatus = enrollmentStatusCounts?.[key] || 0
        const isHovered = hoveredStatus === key

        return (
          <div
            key={key}
            className={cn('h-full rounded transition-all duration-150 cursor-default', {
              'scale-y-110 z-10': isHovered,
            })}
            style={{
              flex: numForStatus,
              ...BAR_SEGMENT_STYLE[key],
            }}
            onMouseEnter={() => setHoveredStatus(key)}
            onMouseLeave={() => setHoveredStatus(null)}
          />
        )
      })}
    </div>
  )
}
